"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ACTION_LABELS,
  ANSWER_LABELS,
  METRIC_LABELS,
  QUESTION_LABELS,
  QUESTION_ORDER,
  RUNG_LABELS,
  SCORE_LABELS,
  SOURCE_LABELS,
  sourceNote,
  THRESHOLD_MODE_LABELS,
  VENUE_LABELS,
  VERDICT_LABELS,
  type ControlCommand,
  type ControlResult,
  type DashboardState,
  type JudgmentView,
  type Snapshot,
  type ThresholdView,
} from "./types";

const ENGINE_PORT = 8091;

/**
 * Book staleness at which the risk engine stops quoting (MAX_STALE_DATA_AGE_S,
 * default 2s). Shown as a red badge so a frozen feed is visible rather than
 * inferred from a ladder that suddenly reads HOLD.
 */
const STALE_AFTER_S = 2;

/**
 * Where the engine lives, as seen from the browser.
 *
 * Derived from the page's own hostname rather than hardcoded, because this
 * dashboard is served over the LAN and a browser on another machine resolves
 * `127.0.0.1` to ITSELF, not to the host running the engine. Serving the page
 * from 192.168.51.100:3001 means the engine is at 192.168.51.100:8091.
 *
 * NEXT_PUBLIC_ENGINE_URL still overrides, for split-host deployments.
 */
function engineUrl(): string {
  const override = process.env.NEXT_PUBLIC_ENGINE_URL;
  if (override) return override;
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.hostname}:${ENGINE_PORT}`;
  }
  return `http://127.0.0.1:${ENGINE_PORT}`;
}

// ---------------------------------------------------------------------------
// formatting helpers
// ---------------------------------------------------------------------------

const fmt = (x: number | null | undefined, dp = 6): string =>
  typeof x === "number" && Number.isFinite(x) ? x.toFixed(dp) : "—";

/** Money, grouped, so 10205.524 reads as 10,205.52. */
const money = (x: number | null | undefined, dp = 2): string =>
  typeof x === "number" && Number.isFinite(x)
    ? x.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : "—";

const signed = (x: number | null | undefined, dp = 4): string =>
  typeof x === "number" && Number.isFinite(x) ? `${x >= 0 ? "+" : "−"}${Math.abs(x).toFixed(dp)}` : "—";

const pct = (x: number | null | undefined, dp = 2): string =>
  typeof x === "number" && Number.isFinite(x) ? `${x >= 0 ? "+" : "−"}${Math.abs(x).toFixed(dp)}%` : "—";

const ms = (x: number | null | undefined): string => {
  if (typeof x !== "number" || !Number.isFinite(x)) return "—";
  return x >= 1000 ? `${(x / 1000).toFixed(1)}s` : `${Math.round(x)}ms`;
};

const dur = (x: number): string => {
  const s = Math.floor(x / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const pnlColour = (v: number): string => (v >= 0 ? "var(--buy)" : "var(--sell)");

/** Bar colour by semantic meaning. Takes the raw answer key, not the label. */
function barColor(key: string, rawAnswer: string): string {
  const a = rawAnswer.toLowerCase();
  if (key === "direction" || key === "regime") {
    if (a === "up" || a === "trending") return "var(--buy)";
    if (a === "down" || a === "crisis") return "var(--sell)";
    return "var(--faint)";
  }
  return "var(--accent)";
}

/** A score reading's answer index, in Chinese, using the question's own option list. */
function answerText(qid: string, value: number | string): string {
  const raw = String(value);
  return SCORE_LABELS[qid]?.[raw] ?? ANSWER_LABELS[raw] ?? raw;
}

// ---------------------------------------------------------------------------
// panels
// ---------------------------------------------------------------------------

function Header({
  state,
  connected,
  busy,
  onControl,
}: {
  state: DashboardState;
  connected: boolean;
  busy: boolean;
  onControl: (cmd: ControlCommand) => void;
}) {
  const s = state.session;
  const loop = state.loop;

  // How old the book behind the quotes is. A live feed can freeze without the
  // socket closing, and this is the number that says so.
  const rawAge = loop?.features?.dataAgeS;
  const dataAge = typeof rawAge === "number" && Number.isFinite(rawAge) ? rawAge : null;

  return (
    <header className="top">
      <div className="brand">
        ‖ LAYA TRADER <span>· 紙上交易</span>
      </div>
      <span className={`badge ${connected ? "live" : "down"}`}>
        <span className={`dot ${connected ? "on" : "off"}`} /> {connected ? "已連線" : "離線"}
      </span>
      <span className="badge dry">模擬運行 — 不會有任何真實訂單送到交易所</span>

      {/* Market picker. Selecting a market stops and saves the current run,
          then starts a fresh one on the chosen instrument. */}
      <span className="market-picker">
        <label htmlFor="market">市場</label>
        <select
          id="market"
          value={s.marketId ?? ""}
          disabled={busy}
          onChange={(e) => onControl({ action: "start", marketId: e.target.value })}
        >
          {s.markets.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </span>

      {loop && <span className="badge">區塊 {loop.block}</span>}
      {loop && <span className="badge">{loop.config.tickMs}ms 一格</span>}
      <span className="badge">模型 {loop?.client.model ?? "—"}</span>
      {loop && (
        <span className="badge">
          門檻：{THRESHOLD_MODE_LABELS[loop.config.thresholdMode] ?? loop.config.thresholdMode}
        </span>
      )}
      {/* The instrument names are real, the prices are not. Someone comparing the
          dashboard against a live exchange has to be told that here, next to the
          market picker, not only in the docs. */}
      {loop && (
        <span
          className={`badge ${loop.venue === "hyperliquid" ? "live" : "sim"}`}
          title={
            loop.venue === "hyperliquid"
              ? "報價來自 Hyperliquid 的實時公共市場；成交與帳戶仍為模擬"
              : "價格由本機合成產生，並非真實交易所報價"
          }
        >
          {VENUE_LABELS[loop.venue] ?? loop.venue}
          {loop.instrument ? ` · ${loop.instrument}` : ""}
        </span>
      )}
      {loop?.jev && (
        <span
          className={`badge ${loop.jev.gate?.positive ? "live" : "down"}`}
          title={`雙模型閘門：Laya 同 ${loop.jev.model} 都正面先至報價`}
        >
          Jev 閘門：{loop.jev.gate?.positive ? "開" : "關"}
        </span>
      )}
      {loop && dataAge !== null && (
        <span
          className={`badge ${dataAge > STALE_AFTER_S ? "down" : ""}`}
          title={`風控在市場數據超過 ${STALE_AFTER_S} 秒未更新時會暫停報價`}
        >
          資料 {dataAge.toFixed(1)}s 前
        </span>
      )}

      <span className="controls">
        <button disabled={busy || !s.running} onClick={() => onControl({ action: "save" })}>
          儲存
        </button>
        <button disabled={busy || !s.running} onClick={() => onControl({ action: "stop" })}>
          停止
        </button>
        <button
          disabled={busy || s.running}
          className="primary"
          onClick={() => onControl({ action: "start", marketId: s.marketId ?? s.markets[0]?.id })}
        >
          {s.running ? "運行中" : "開始"}
        </button>
      </span>
    </header>
  );
}

function SessionPanel({
  state,
  busy,
  onControl,
}: {
  state: DashboardState;
  busy: boolean;
  onControl: (cmd: ControlCommand) => void;
}) {
  const s = state.session;
  const saved = s.saved;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="panel">
        <h2>交易階段已停止</h2>
        <p style={{ color: "var(--dim)", margin: "0 0 10px" }}>
          {s.marketLabel ?? "未選擇市場"}。請在標題列選擇市場後按「開始」，或在下方恢復已儲存的紀錄。
        </p>
        <div className="row">
          <span className="k">上次階段</span>
          <span className="v">{s.sessionId ?? "—"}</span>
        </div>
        <div className="row">
          <span className="k">已儲存紀錄</span>
          <span className="v">{saved.length}</span>
        </div>
      </div>

      <div className="panel">
        <h2>已儲存的交易階段</h2>
        {saved.length === 0 ? (
          <div className="empty">尚未有任何紀錄。交易進行中按「儲存」即會寫入。</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>市場</th>
                <th>儲存時間</th>
                <th style={{ textAlign: "right" }}>權益</th>
                <th style={{ textAlign: "right" }}>淨損益</th>
                <th style={{ textAlign: "right" }}>報酬</th>
                <th style={{ textAlign: "right" }}>成交</th>
                <th style={{ textAlign: "right" }}>持倉</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {saved.map((x) => {
                // The account's opening balance is equity minus the P&L it made.
                const opening = x.summary.equity - x.summary.sessionPnl;
                const ret = opening > 0 ? (x.summary.sessionPnl / opening) * 100 : null;
                return (
                  <tr key={x.id}>
                    <td>{x.symbol}</td>
                    <td style={{ color: "var(--dim)" }}>{new Date(x.savedAtMs).toLocaleString("zh-HK")}</td>
                    <td style={{ textAlign: "right" }}>{money(x.summary.equity)}</td>
                    <td style={{ textAlign: "right", color: pnlColour(x.summary.sessionPnl) }}>
                      {signed(x.summary.sessionPnl)}
                    </td>
                    <td style={{ textAlign: "right", color: pnlColour(x.summary.sessionPnl) }}>{pct(ret)}</td>
                    <td style={{ textAlign: "right" }}>{x.summary.fills}</td>
                    <td style={{ textAlign: "right" }}>{x.summary.position.toFixed(1)}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {/* Resuming across markets is refused server-side: position
                          units and price scale differ, so the basis would be
                          meaningless. Disable rather than let it fail. */}
                      <button
                        disabled={busy || x.marketId !== s.marketId}
                        title={
                          x.marketId === s.marketId
                            ? "以同一市場接續這個帳戶"
                            : `請先切換到 ${x.symbol} — 持倉無法跨價格尺度轉移`
                        }
                        onClick={() => onControl({ action: "start", marketId: x.marketId, resumeFrom: x.id })}
                      >
                        恢復
                      </button>{" "}
                      <button disabled={busy} onClick={() => onControl({ action: "delete", id: x.id })}>
                        刪除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="mono-dim" style={{ marginTop: 8 }}>
          恢復會接續該帳戶的持倉、現金與入場均價，但只限同一個市場：持倉以該商品的單位計價，跨商品的價格尺度不同，入場基準會失去意義。
        </p>
      </div>
    </div>
  );
}

function StatRow({ snap }: { snap: Snapshot }) {
  const s = snap.stats;
  const c = snap.client;
  const p = snap.portfolio;
  const x = snap.pnl;
  const t = snap.trades;

  // Total gain/loss is money in the account: equity now against the opening
  // balance. `startingCash` is on the wire from current engines; older ones can
  // still be read exactly from equity minus the session P&L.
  const opening = x?.startingCash ?? p.equity - p.dayPnl;
  const total = p.equity - opening;
  const ret = opening > 0 ? (total / opening) * 100 : null;

  // Buy/sell split: the account's lifetime counters when the engine reports
  // them, otherwise the retained window's own counts, labelled as such.
  const windowBuys = snap.fills.filter((f) => f.side === "buy").length;
  const windowSells = snap.fills.length - windowBuys;
  const splitKnown = t?.known === true;

  return (
    <div className="grid cols-4">
      <div className="panel stat">
        <span className="k">淨損益（帳戶賺蝕）</span>
        <span className="v" style={{ color: pnlColour(total) }}>
          {signed(total, 2)}
        </span>
        <span className="mono-dim" style={{ color: pnlColour(total) }}>
          {pct(ret)} · 起始 {money(opening)}
        </span>
      </div>
      <div className="panel stat">
        <span className="k">成交</span>
        {/* The account's lifetime total when the engine reports it; otherwise the
            retained feed's size, marked as the sample it is. A resumed account
            carries the previous run's fills in the feed but not their totals. */}
        <span className="v">{splitKnown ? t.fills : snap.fills.length}</span>
        <span className="mono-dim">
          {splitKnown
            ? `買 ${t.buys} · 賣 ${t.sells}（帳戶累計）`
            : `買 ${windowBuys} · 賣 ${windowSells}（本頁樣本）`}
        </span>
        <span className="mono-dim">
          本次運行 {s.fills} 筆 · {s.decisions} 次決策 · {s.lateTicks} 次落後
        </span>
      </div>
      <div className="panel stat">
        <span className="k">未平倉（浮動）</span>
        <span className="v" style={{ color: x ? pnlColour(x.unrealized) : undefined }}>
          {signed(x?.unrealized)}
        </span>
        <span className="mono-dim">
          持倉 {p.position.toFixed(1)} · 入場均價 {x && x.avgEntry > 0 ? x.avgEntry.toFixed(6) : "—"}
        </span>
      </div>
      <div className="panel stat">
        <span className="k">運行時間</span>
        <span className="v small">{dur(snap.uptimeMs)}</span>
        <span className="mono-dim">
          最近判斷 {ms(s.lastLatencyMs)}（平均 {ms(c.avgLatencyMs)}）
        </span>
        <span className="mono-dim">
          {c.timeouts} 次超時 · {s.modelErrors} 次錯誤 · {s.fallbackJudgments} 次後備
        </span>
      </div>
    </div>
  );
}

function PriceChart({ snap }: { snap: Snapshot }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const points = snap.equityCurve;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    if (points.length < 2) {
      ctx.fillStyle = "#5c636d";
      ctx.font = "12px ui-monospace, monospace";
      ctx.fillText("等待數據…", 8, h / 2);
      return;
    }

    const pad = 6;
    const x = (i: number) => (i / (points.length - 1)) * (w - 8) + 4;

    // Two series on two scales, because they answer different questions: the mid
    // is what the MARKET did, equity is what the ACCOUNT did. Plotting only the
    // mid next to a P&L figure is how a -9% price move came to read like a loss.
    const mids = points.map((p) => p.mid);
    const midLo = Math.min(...mids);
    const midHi = Math.max(...mids);
    const midSpan = midHi - midLo || 1;
    const yMid = (v: number) => h - 10 - ((v - midLo) / midSpan) * (h - 24);

    const equities = points.map((p) => p.equity);
    const eqLo = Math.min(...equities);
    const eqHi = Math.max(...equities);
    const eqSpan = eqHi - eqLo || 1;
    const yEq = (v: number) => h - 10 - ((v - eqLo) / eqSpan) * (h - 24);

    // Mid, filled, on the left axis.
    ctx.beginPath();
    ctx.moveTo(x(0), h);
    for (let i = 0; i < points.length; i++) ctx.lineTo(x(i), yMid(mids[i] as number));
    ctx.lineTo(x(points.length - 1), h);
    ctx.closePath();
    ctx.fillStyle = "rgba(106,169,255,0.08)";
    ctx.fill();

    ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      const px = x(i);
      const py = yMid(mids[i] as number);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.strokeStyle = "#6aa9ff";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Equity, on its own scale (the right-hand axis).
    ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      const px = x(i);
      const py = yEq(equities[i] as number);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.strokeStyle = "#f0b429";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Scale markers, so "which line is which" is answered on the chart itself.
    ctx.fillStyle = "#6aa9ff";
    ctx.font = "11px ui-monospace, monospace";
    ctx.fillText(`中間價 ${midHi.toPrecision(6)}`, 6, 12);
    ctx.fillText(`中間價 ${midLo.toPrecision(6)}`, 6, h - pad);
    ctx.fillStyle = "#f0b429";
    ctx.textAlign = "right";
    ctx.fillText(`權益 ${money(eqHi)}`, w - 6, 12);
    ctx.fillText(`權益 ${money(eqLo)}`, w - 6, h - pad);
    ctx.textAlign = "left";

    // Our resting quotes, so the reader can see where we sit relative to the mid.
    const bid = snap.restingOrders.find((o) => o.side === "buy");
    const ask = snap.restingOrders.find((o) => o.side === "sell");
    for (const [order, colour] of [[bid, "#35d07f"], [ask, "#ff5c5c"]] as const) {
      if (!order) continue;
      const oy = yMid(order.price);
      if (oy < 0 || oy > h) continue;
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.moveTo(0, oy);
      ctx.lineTo(w, oy);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [points, snap.restingOrders]);

  const first = points[0];
  const last = points[points.length - 1];
  const priceChange = first && last && first.mid ? ((last.mid - first.mid) / first.mid) * 100 : null;
  const equityChange = first && last && first.equity ? ((last.equity - first.equity) / first.equity) * 100 : null;
  const windowMin = first && last ? Math.max(1, Math.round((last.tsMs - first.tsMs) / 60_000)) : null;

  return (
    <div className="panel">
      <h2>
        {snap.symbol} — 中間價 {fmt(snap.mid)} · 價差 {snap.spreadBps.toFixed(1)} bps
      </h2>
      <p className="mono-dim" style={{ margin: "0 0 6px" }}>
        <span style={{ color: "#6aa9ff" }}>━ 中間價（市場價格，左軸）</span>{" "}
        <span style={{ color: "#f0b429" }}>━ 權益（帳戶淨值，右軸）</span>
        {windowMin !== null && priceChange !== null && (
          <>
            {" · "}區間約 {windowMin} 分鐘：價格 <span style={{ color: pnlColour(priceChange) }}>{pct(priceChange)}</span>
            {" · "}權益 <span style={{ color: pnlColour(equityChange ?? 0) }}>{pct(equityChange)}</span>
          </>
        )}
      </p>
      <canvas ref={ref} />
      <p className="mono-dim" style={{ marginTop: 6 }}>
        {sourceNote(snap.venue, snap.symbol, snap.instrument ?? null)}
      </p>
    </div>
  );
}

function ProbabilityBars({ reading, qid }: { reading: JudgmentView["readings"][string]; qid: string }) {
  if (!reading.probabilities) return null;
  const entries = Object.entries(reading.probabilities).sort((a, b) => b[1] - a[1]);
  return (
    <div style={{ marginTop: 4 }}>
      {entries.map(([rawAnswer, p]) => (
        <div key={rawAnswer} className="row" style={{ padding: "2px 0", alignItems: "center" }}>
          <span style={{ color: "var(--dim)", minWidth: 130, fontSize: 11 }}>
            {answerText(qid, rawAnswer)}
          </span>
          <span style={{ flex: 1, margin: "0 8px" }}>
            <span className="bar-track" style={{ display: "block" }}>
              <span
                className="bar-fill"
                style={{ width: `${(p * 100).toFixed(1)}%`, background: barColor(qid, rawAnswer) }}
              />
            </span>
          </span>
          <span style={{ minWidth: 48, textAlign: "right", fontSize: 11 }}>{(p * 100).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

function JudgmentPanel({
  snap,
  busy,
  onControl,
}: {
  snap: Snapshot;
  busy: boolean;
  onControl: (cmd: ControlCommand) => void;
}) {
  const rows = QUESTION_ORDER.map((qid) => {
    // Prefer the full battery; fall back to the fast tier for the two it shares.
    const reading = snap.full?.readings[qid] ?? snap.fast?.readings[qid];
    return { qid, reading };
  });

  const age = snap.full?.ageMs ?? Number.POSITIVE_INFINITY;
  const fullStale = age > snap.config.maxFullAgeMs;
  const fastAge = snap.fast?.ageMs ?? Number.POSITIVE_INFINITY;
  const fastStale = fastAge > snap.config.maxFastAgeMs;

  return (
    <div className="panel">
      <h2>
        判斷電池 · 完整層 {snap.full ? `${snap.full.model} ${ms(snap.full.latencyMs)}` : "—"} ·{" "}
        <span style={{ color: fullStale ? "var(--sell)" : "var(--dim)" }}>已 {ms(age)} 未更新</span>
      </h2>
      {snap.jev && (
        <>
          {(() => {
            const j = snap.jev;
            const hasJudgment = Number.isFinite(j.ageMs);
            const maxAge = j.maxAgeMs ?? 60_000;
            const waiting = (j.nextCallInMs ?? 0) > 0;
            // 分三態：有判斷（正常）、等下一次呼叫（配額節流，唔係故障）、
            // 判斷過期（真問題 — 表示呼叫一直失敗）。
            const stale = hasJudgment && j.ageMs > maxAge;
            const state = !hasJudgment
              ? { label: "尚無判斷", colour: "var(--warn)" }
              : stale
                ? { label: `判斷過期（${ms(j.ageMs)}）`, colour: "var(--sell)" }
                : { label: "判斷有效", colour: "var(--buy)" };
            return (
              <>
                <div className="row">
                  <span className="k">Jev 閘門</span>
                  <span className="v" style={{ color: j.enabled === false ? "var(--dim)" : j.gate?.positive ? "var(--buy)" : "var(--sell)" }}>
                    {j.enabled === false ? "已停用" : j.gate?.positive ? "✓ 開" : "✗ 關"} · {j.model}
                  </span>
                  {/* 即時切換：唔使重啟交易階段。停用 = 單模型模式（只跟 Laya）。 */}
                  <button
                    disabled={busy}
                    title={
                      j.enabled === false
                        ? "啟用 Jev 閘門：Laya 同 Jev 都正面先至開新報價"
                        : "停用 Jev 閘門：改為只跟 Laya（唔會停止 Laya）"
                    }
                    onClick={() => onControl({ action: "jev", enabled: j.enabled === false })}
                    style={{ marginLeft: 8, padding: "2px 8px", fontSize: 11 }}
                  >
                    {j.enabled === false ? "啟用" : "停用"}
                  </button>
                </div>
                <div className="row">
                  <span className="k">Jev 狀態</span>
                  <span className="v" style={{ color: state.colour, fontSize: 11 }}>
                    {state.label}
                    {j.latencyMs > 0 ? ` · 延遲 ${ms(j.latencyMs)}` : ""}
                    {waiting ? ` · ${Math.ceil((j.nextCallInMs ?? 0) / 1000)}s 後再問` : ""}
                    {j.consecutiveErrors > 0
                      ? ` · 連續 ${j.consecutiveErrors} 次失敗`
                      : ""}
                  </span>
                </div>
                {j.consecutiveErrors > 0 && (
                  <div className="row">
                    <span className="k">Jev 錯誤</span>
                    <span className="v" style={{ color: "var(--warn)", fontSize: 11 }}>
                      免費層有總量配額，用完會回 HTTP 429 並要求等一段時間；引擎會跟住退避，
                      期間閘門維持關閉（唔開新報價，但減倉照做）。
                    </span>
                  </div>
                )}
              </>
            );
          })()}
          {snap.jev.enabled !== false && snap.jev.gate && !snap.jev.gate.positive && (
            <div className="row">
              <span className="k">閘門原因</span>
              <span className="v" style={{ color: "var(--sell)", fontSize: 11 }}>
                {snap.jev.gate.reason} — 只減倉、不開新報價
              </span>
            </div>
          )}
          {snap.jev.enabled === false && (
            <div className="row">
              <span className="k">閘門狀態</span>
              <span className="v" style={{ color: "var(--dim)", fontSize: 11 }}>
                已停用 — 只跟 Laya 判斷報價，Jev 唔參與決策（按「啟用」可隨時恢復）
              </span>
            </div>
          )}
        </>
      )}
      <div className="row">
        <span className="k">快速層</span>
        <span className="v" style={{ color: fastStale ? "var(--sell)" : "var(--buy)" }}>
          {snap.fast
            ? `${SOURCE_LABELS[snap.fast.source] ?? snap.fast.source} · 已 ${ms(fastAge)} 未更新`
            : "—"}
        </span>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{ width: 150 }}>問題</th>
            <th style={{ width: 130 }}>Laya</th>
            {snap.jev && <th style={{ width: 110 }}>Jev</th>}
            <th>分佈</th>
            <th style={{ width: 70, textAlign: "right" }}>信心</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ qid, reading }) => (
            <tr key={qid}>
              <td>{QUESTION_LABELS[qid]}</td>
              <td>
                {reading ? (
                  reading.type === "noul" ? (
                    <strong>{(reading.value as number).toFixed(3)}</strong>
                  ) : (
                    <strong>{answerText(qid, reading.value)}</strong>
                  )
                ) : (
                  <span style={{ color: "var(--faint)" }}>未詢問</span>
                )}
              </td>
              {snap.jev && (
                <td>
                  {(() => {
                    // 閘門只用 Jev 兩個讀數（環境、有毒流量），其他顯示 —。
                    const jr = snap.jev?.view?.readings?.[qid];
                    if (!jr) return <span style={{ color: "var(--faint)" }}>—</span>;
                    const isGate = qid === "quote_environment" || qid === "toxic_flow";
                    return (
                      <strong style={{ color: isGate ? "var(--accent)" : "var(--dim)" }}>
                        {jr.type === "noul" ? (jr.value as number).toFixed(2) : answerText(qid, jr.value)}
                      </strong>
                    );
                  })()}
                </td>
              )}
              <td>{reading ? <ProbabilityBars reading={reading} qid={qid} /> : null}</td>
              <td style={{ textAlign: "right" }}>{reading ? reading.confidence.toFixed(2) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mono-dim" style={{ marginTop: 8 }}>
        信心值是 Laya 的熵尺度，不是 Jev 的。留意數值範圍極窄：在平靜與危機市況下量度，這些數值只相差百分之幾。
        {snap.jev && (
          <>
            {" "}
            <strong>Jev 欄</strong>是第二個模型對同一份狀態的答案；閘門只採用佢嘅
            <strong>報價環境</strong>（要 ≥ 2）同 <strong>有毒流量</strong>（要 &lt; 0.5）兩項，
            兩項都過先至准開新報價。
          </>
        )}
      </p>
    </div>
  );
}

function DecisionPanel({ snap }: { snap: Snapshot }) {
  const a = snap.action;
  const ladder = snap.ladder;
  return (
    <div className="panel">
      <h2>決策</h2>
      {ladder && (
        <div className="quote-state">
          <div className={`rung ${ladder.rung}`}>{RUNG_LABELS[ladder.rung] ?? ladder.rung}</div>
          <div style={{ color: "var(--dim)" }}>{ladder.reason}</div>
        </div>
      )}
      <div className="row">
        <span className="k">動作</span>
        <span className="v">
          <span className={`tag ${a?.kind === "quote" ? "ok" : a?.kind === "kill" ? "bad" : "warn"}`}>
            {a ? (ACTION_LABELS[a.kind] ?? a.kind) : "—"}
          </span>{" "}
          {a?.late ? <span className="tag bad">落後</span> : null}
        </span>
      </div>
      <div className="row">
        <span className="k">買價 / 賣價</span>
        <span className="v">
          <span style={{ color: "var(--buy)" }}>{fmt(a?.bid)}</span> /{" "}
          <span style={{ color: "var(--sell)" }}>{fmt(a?.ask)}</span>
        </span>
      </div>
      <div className="row">
        <span className="k">數量</span>
        <span className="v">{a ? a.size.toFixed(1) : "—"}</span>
      </div>
      <div className="row">
        <span className="k">來源</span>
        <span className="v">{a ? (SOURCE_LABELS[a.source] ?? a.source) : "—"}</span>
      </div>
      {snap.sizing && (
        <div className="row">
          <span className="k">大小參數</span>
          <span className="v" style={{ fontSize: 11 }}>
            kelly {snap.sizing.terms.kelly?.toFixed(3)} · 有利度{" "}
            {snap.sizing.terms.favourableScale?.toFixed(2)} · 信心{" "}
            {snap.sizing.terms.confidenceScale?.toFixed(2)} · 持倉{" "}
            {snap.sizing.terms.inventoryScale?.toFixed(2)}
          </span>
        </div>
      )}
      <p className="mono-dim" style={{ marginTop: 8 }}>
        {a?.reason ?? "—"}
      </p>
    </div>
  );
}

function ThresholdRow({ name, t }: { name: string; t: ThresholdView }) {
  const label = METRIC_LABELS[name] ?? QUESTION_LABELS[name] ?? name;
  return (
    <tr>
      <td>{label}</td>
      <td style={{ textAlign: "right" }}>{t.value.toFixed(3)}</td>
      <td style={{ textAlign: "right", color: "var(--dim)" }}>{t.z.toFixed(2)}</td>
      <td style={{ textAlign: "right", color: "var(--dim)" }}>
        {t.threshold.toFixed(2)}
        {t.absoluteThreshold !== undefined && Number.isFinite(t.absoluteThreshold) ? (
          <span style={{ color: "var(--faint)" }}> / {t.absoluteThreshold.toFixed(2)}</span>
        ) : null}
      </td>
      <td style={{ textAlign: "right" }}>
        <span className={`tag ${t.triggered ? (t.firedBy === "absolute" ? "bad" : "warn") : "dim"}`}>
          {t.triggered ? (t.firedBy === "absolute" ? "下限" : "Z") : "—"}
        </span>
      </td>
    </tr>
  );
}

function ThresholdPanel({ snap }: { snap: Snapshot }) {
  if (!snap.thresholds) return null;
  const anyBaselineReady = Object.values(snap.baseline).some((b) => b.n >= 20);
  return (
    <div className="panel">
      <h2>
        政策門檻 · 模式 {THRESHOLD_MODE_LABELS[snap.config.thresholdMode] ?? snap.config.thresholdMode}
      </h2>
      <table>
        <thead>
          <tr>
            <th>讀數</th>
            <th style={{ textAlign: "right" }}>數值</th>
            <th style={{ textAlign: "right" }}>z</th>
            <th style={{ textAlign: "right" }}>z / 下限</th>
            <th style={{ textAlign: "right" }}>觸發來源</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(snap.thresholds).map(([name, t]) => (
            <ThresholdRow key={name} name={name} t={t} />
          ))}
        </tbody>
      </table>
      <p className="mono-dim" style={{ marginTop: 8 }}>
        混合模式：讀數可以因<strong>新異</strong>（z 相對自身滾動基準）或<strong>下限</strong>（非自適應）而觸發。z 檢定捕捉突變；下限捕捉基線已經吸收了的持續狀態 — 正是這個設計漏洞，讓系統在有毒跌勢中 z 讀數為 -0.92 時仍然繼續報價。
        {!anyBaselineReady && " 基線尚未建立，現時只有下限生效。"}
      </p>
    </div>
  );
}

function PnlPanel({ snap }: { snap: Snapshot }) {
  const p = snap.portfolio;
  const x = snap.pnl;
  const gapBad = x ? Math.abs(x.reconciliationGap) > 1e-6 : false;

  // Gross comes from current engines directly; for older ones it is still exact,
  // because net realised is gross minus the fees already charged to it.
  const gross = x ? (x.grossRealized ?? x.realized + x.fees) : null;
  const opening = x?.startingCash ?? p.equity - p.dayPnl;
  const total = p.equity - opening;
  const ret = opening > 0 ? (total / opening) * 100 : null;

  return (
    <div className="panel">
      <h2>損益拆解</h2>

      <div className="row">
        <span className="k">
          <strong>淨損益（總盈虧）</strong>
        </span>
        <span className="v" style={{ color: pnlColour(total), fontSize: 18 }}>
          <strong>{signed(total, 2)}</strong>{" "}
          <span style={{ fontSize: 13 }}>({pct(ret)})</span>
        </span>
      </div>
      <div className="row">
        <span className="k">起始權益 → 現時權益</span>
        <span className="v">
          {money(opening)} → {money(p.equity)}
        </span>
      </div>
      <div className="row">
        <span className="k">現時持倉 · 入場均價</span>
        <span className="v">
          {p.position.toFixed(1)} · {x && x.avgEntry > 0 ? x.avgEntry.toFixed(6) : "—"}
        </span>
      </div>

      {x && (
        <>
          <div className="row" style={{ borderTop: "1px solid #22262c", marginTop: 8, paddingTop: 8 }}>
            <span className="k">已平倉毛利（未扣手續費）</span>
            <span className="v" style={{ color: pnlColour(gross ?? 0) }}>{signed(gross)}</span>
          </div>
          <div className="row">
            <span className="k">− 手續費</span>
            <span className="v" style={{ color: "var(--dim)" }}>−{x.fees.toFixed(4)}</span>
          </div>
          <div className="row">
            <span className="k">= 已平倉淨額</span>
            <span className="v" style={{ color: pnlColour(x.realized) }}>{signed(x.realized)}</span>
          </div>
          <div className="row">
            <span className="k">+ 未平倉（浮動）</span>
            <span className="v" style={{ color: pnlColour(x.unrealized) }}>{signed(x.unrealized)}</span>
          </div>
          <div className="row">
            <span className="k">= 淨損益</span>
            <span className="v" style={{ color: pnlColour(total) }}>{signed(total)}</span>
          </div>
          <div className="row">
            <span className="k">會計核對</span>
            <span className="v">
              <span className={`tag ${gapBad ? "bad" : "ok"}`}>
                {gapBad ? `有差距 ${x.reconciliationGap.toFixed(6)}` : "已對平"}
              </span>
            </span>
          </div>
        </>
      )}

      <p className="mono-dim" style={{ marginTop: 8 }}>
        已平倉淨額已經扣除了手續費，不需要再減一次 — 之前的版面把「手續費」列在「已實現」旁邊，令人誤以為要相減，這是今次重新拆解的原因。已平倉淨額 + 未平倉必須等於淨損益；出現差距代表入場基準已經漂移，以上數字都不可信。這個核對之所以存在，是因為早前曾有一個缺陷：帳戶實際上虧損，卻報告 +32.28 已實現盈利。
      </p>
    </div>
  );
}

function RiskPanel({ snap }: { snap: Snapshot }) {
  const r = snap.risk;
  const p = snap.portfolio;
  const x = snap.pnl;
  const dd = p.peakEquity > 0 ? (p.peakEquity - p.equity) / p.peakEquity : 0;
  const verdictClass = r?.verdict === "OK" ? "ok" : r?.verdict === "KILL" ? "bad" : "warn";
  return (
    <div className="panel">
      <h2>風控 · 確定性否決</h2>
      <div className="row">
        <span className="k">裁定</span>
        <span className="v">
          <span className={`tag ${verdictClass}`}>
            {r ? (VERDICT_LABELS[r.verdict] ?? r.verdict) : "—"}
          </span>
        </span>
      </div>
      <div className="row">
        <span className="k">持倉</span>
        <span className="v">{p.position.toFixed(1)}</span>
      </div>
      <div className="row">
        <span className="k">回撤</span>
        <span className="v">{(dd * 100).toFixed(2)}%</span>
      </div>
      <div className="row">
        <span className="k">持倉時間</span>
        <span className="v">{ms(p.inventoryAgeMs)}</span>
      </div>
      <div className="row">
        <span className="k">已平倉淨額</span>
        <span className="v" style={{ color: pnlColour(x?.realized ?? p.realizedPnl) }}>
          {signed(x?.realized ?? p.realizedPnl)}
        </span>
      </div>
      <div className="row">
        <span className="k">掛單中</span>
        <span className="v">
          {snap.restingOrders.length === 0
            ? "無"
            : snap.restingOrders.map((o) => `${o.side === "buy" ? "買" : "賣"} ${o.size.toFixed(0)}@${o.price.toFixed(6)}`).join(" · ")}
        </span>
      </div>
      {r && r.reasons.length > 0 && (
        <p className="mono-dim" style={{ marginTop: 8 }}>
          {r.reasons.join(" ｜ ")}
        </p>
      )}
    </div>
  );
}

function FillFeed({ snap }: { snap: Snapshot }) {
  // The wire carries the tail of the fill log, so the panel must say which slice
  // it is showing and put the account's own totals next to it. Reading a losing
  // streak of 40 rows as "the account is losing" is the mistake this panel made
  // possible: those rows can be 2% of the session.
  const all = snap.fills;
  const shown = [...all].reverse().slice(0, 50);
  const t = snap.trades;
  const splitKnown = t?.known === true;
  const buys = all.filter((f) => f.side === "buy").length;

  const sumGross = shown.reduce((a, f) => a + f.realized, 0);
  const sumFees = shown.reduce((a, f) => a + f.fee, 0);
  const sumNet = sumGross - sumFees;

  return (
    <div className="panel">
      <h2>成交紀錄</h2>

      <div className="row">
        <span className="k">本次運行成交</span>
        <span className="v">{snap.stats.fills} 筆</span>
      </div>
      <div className="row">
        <span className="k">帳戶累計成交</span>
        <span className="v">
          {splitKnown
            ? `${t.fills} 筆 · 買 ${t.buys} · 賣 ${t.sells}`
            : t
              ? "無法追溯（此帳戶由舊版存檔恢復）"
              : "—（引擎下次啟動後提供）"}
        </span>
      </div>
      <div className="row">
        <span className="k">累計成交量（基礎貨幣）</span>
        <span className="v">
          {splitKnown ? `買 ${t.buyVolume.toFixed(1)} · 賣 ${t.sellVolume.toFixed(1)}` : "—"}
        </span>
      </div>
      <div className="row">
        <span className="k">本頁樣本</span>
        <span className="v">
          最近 {all.length} 筆 · 買 {buys} · 賣 {all.length - buys}
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="empty">尚未有成交。</div>
      ) : (
        <div className="fills">
          <table>
            <thead>
              <tr>
                <th>區塊</th>
                <th>方向</th>
                <th style={{ textAlign: "right" }}>價格</th>
                <th style={{ textAlign: "right" }}>數量</th>
                <th style={{ textAlign: "right" }}>名義值</th>
                <th style={{ textAlign: "right" }}>毛利</th>
                <th style={{ textAlign: "right" }}>手續費</th>
                <th style={{ textAlign: "right" }}>淨額</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f, i) => {
                const net = f.realized - f.fee;
                return (
                  <tr key={`${f.tsMs}-${i}`}>
                    <td style={{ color: "var(--dim)" }}>{f.seq}</td>
                    <td style={{ color: f.side === "buy" ? "var(--buy)" : "var(--sell)" }}>
                      {f.side === "buy" ? "買" : "賣"}
                    </td>
                    <td style={{ textAlign: "right" }}>{f.price.toFixed(6)}</td>
                    <td style={{ textAlign: "right" }}>{f.size.toFixed(1)}</td>
                    <td style={{ textAlign: "right", color: "var(--dim)" }}>
                      {(f.price * f.size).toFixed(2)}
                    </td>
                    <td style={{ textAlign: "right", color: f.realized === 0 ? "var(--faint)" : pnlColour(f.realized) }}>
                      {f.realized === 0 ? "0.00000" : signed(f.realized, 5)}
                    </td>
                    <td style={{ textAlign: "right", color: "var(--dim)" }}>−{f.fee.toFixed(5)}</td>
                    <td style={{ textAlign: "right", color: f.realized === 0 ? "var(--faint)" : pnlColour(net) }}>
                      {f.realized === 0 ? "—" : signed(net, 5)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ color: "var(--dim)" }}>
                <td colSpan={5}>本頁合計（最近 {shown.length} 筆）</td>
                <td style={{ textAlign: "right", color: pnlColour(sumGross) }}>{signed(sumGross, 5)}</td>
                <td style={{ textAlign: "right" }}>−{sumFees.toFixed(5)}</td>
                <td style={{ textAlign: "right", color: pnlColour(sumNet) }}>{signed(sumNet, 5)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="mono-dim" style={{ marginTop: 8 }}>
        毛利＝該筆成交平倉時的賺蝕，未扣手續費；淨額＝毛利 − 手續費。開倉或加倉的成交毛利是 0，盈虧會在平倉時才入帳，所以大部分成交列都會顯示 0。
        上表只是最近若干筆，帳戶的總數見上方「成交」欄及「損益拆解」。
      </p>
    </div>
  );
}

function StatePanel({ snap }: { snap: Snapshot }) {
  return (
    <div className="panel">
      <h2>送往模型的狀態描述</h2>
      <p style={{ color: "var(--dim)", fontSize: 12, margin: "0 0 8px" }}>{snap.state || "—"}</p>
      <p className="mono-dim">
        這一段刻意保留英文原文：它是逐字送往 Laya 的輸入，翻譯會改變模型被問的問題。以散文而非 JSON 呈現，是因為實測四種狀態寫法之下，散文的辨別力與最佳 JSON 版本相同，但少用 22% token；而字段詞彙表的表現最差。
      </p>
    </div>
  );
}

function CalibrationPanel({ snap }: { snap: Snapshot }) {
  const entries = Object.entries(snap.baseline).filter(([, b]) => b.n >= 20);
  return (
    <div className="panel">
      <h2>校準</h2>
      <div className="row">
        <span className="k">待結算</span>
        <span className="v">{snap.calibration.pending}</span>
      </div>
      <div className="row">
        <span className="k">寫入失敗</span>
        <span className="v">{snap.calibration.failures}</span>
      </div>
      <p className="mono-dim" style={{ marginTop: 8 }}>
        6 個讀數中有 {entries.length} 個已建立可用基線（n≥20）。執行 <code>npm run calibrate</code>{" "}
        可擬合可靠度曲線。至今量度結果：沒有任何讀數的表現優於其基準率，所以門檻維持相對而非絕對。
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

export default function Page() {
  const [state, setState] = useState<DashboardState | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [engine, setEngine] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const url = engineUrl();
    setEngine(url);
    const source = new EventSource(`${url}/stream`);
    source.addEventListener("open", () => {
      setConnected(true);
      setError(null);
    });
    source.addEventListener("snapshot", (ev) => {
      try {
        setState(JSON.parse((ev as MessageEvent).data) as DashboardState);
        setConnected(true);
      } catch {
        /* a malformed frame should not tear down the stream */
      }
    });
    source.addEventListener("error", () => {
      setConnected(false);
      setError(
        `無法連接引擎 ${url}。請在執行此頁面的主機上以 npm run engine 啟動引擎。`,
      );
    });
    return () => source.close();
  }, []);

  const onControl = useCallback(
    (cmd: ControlCommand) => {
      setBusy(true);
      setNotice(null);
      void (async () => {
        try {
          const res = await fetch(`${engineUrl()}/control`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(cmd),
          });
          const result = (await res.json()) as ControlResult;
          setNotice(result.message);
          if (!result.ok) setError(result.message);
          else setError(null);
        } catch (err) {
          setNotice(`控制指令失敗：${err instanceof Error ? err.message : String(err)}`);
        } finally {
          // The SSE stream carries the new state; this only releases the buttons.
          setBusy(false);
        }
      })();
    },
    [],
  );

  const loop = state?.loop ?? null;

  return (
    <>
      {state && (
        <Header state={state} connected={connected} busy={busy} onControl={onControl} />
      )}
      <main>
        {error && (
          <div className="panel" style={{ borderColor: "#4a1f1f" }}>
            <h2>無法連接引擎</h2>
            <p style={{ color: "var(--sell)" }}>{error}</p>
            {engine && <p className="mono-dim">引擎位址：{engine}</p>}
          </div>
        )}
        {notice && !error && (
          <div className="panel" style={{ borderColor: "#22402f", marginBottom: 14 }}>
            <p style={{ color: "var(--buy)", margin: 0 }}>{notice}</p>
          </div>
        )}
        {!state ? (
          <div className="panel">
            <div className="empty">等待第一個快照…</div>
          </div>
        ) : !loop ? (
          <SessionPanel state={state} busy={busy} onControl={onControl} />
        ) : (
          <div className="grid" style={{ gap: 14 }}>
            <StatRow snap={loop} />
            <PriceChart snap={loop} />
            <PnlPanel snap={loop} />
            <div className="grid cols-2">
              <DecisionPanel snap={loop} />
              <RiskPanel snap={loop} />
            </div>
            <FillFeed snap={loop} />
            <JudgmentPanel snap={loop} busy={busy} onControl={onControl} />
            <div className="grid cols-2">
              <ThresholdPanel snap={loop} />
              <CalibrationPanel snap={loop} />
            </div>
            <StatePanel snap={loop} />
          </div>
        )}
      </main>
    </>
  );
}
